<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import dealReportsAPI from 'dashboard/api/dealReports';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import LineChart from 'shared/components/charts/DashboardLineChart.vue';
import { formatCurrencyBRL } from '../Comercial/commercialFormatters';
import { lineDataset, lineOptions } from '../charts/chartDesign';
import { useChartTheme } from '../charts/useChartTheme';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const theme = useChartTheme();
// Aberta por padrão; se o usuário recolher, continua recolhida até ele abrir.
const isVisible = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.MEU_ESPACO_EVOLUTION_VISIBLE) !== false
);
const data = ref(null);
const isLoading = ref(false);
const hasError = ref(false);

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await dealReportsAPI.getMyEvolution(props.params);
    data.value = response.data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const toggleVisible = () => {
  isVisible.value = !isVisible.value;
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.MEU_ESPACO_EVOLUTION_VISIBLE,
    isVisible.value
  );
  if (isVisible.value) fetchData();
};

watch(
  () => props.params,
  () => {
    if (isVisible.value) fetchData();
  },
  { deep: true, immediate: true }
);

const series = computed(() => data.value?.series || []);

const chartCollection = computed(() => ({
  labels: series.value.map(row => format(parseISO(row.date), 'dd/MM')),
  datasets: [
    lineDataset({
      data: series.value.map(row => row.revenue),
      label: t('DASHBOARD.COMERCIAL.EVOLUTION.REVENUE'),
      theme,
    }),
  ],
}));

const chartOptions = computed(() =>
  lineOptions({ theme: theme.value, formatTick: formatCurrencyBRL })
);
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <div class="flex items-center justify-between p-3">
      <h2 class="font-medium text-n-slate-12">
        {{ t('DASHBOARD.MEU_ESPACO.SECTIONS.EVOLUTION') }}
      </h2>
      <button
        type="button"
        class="px-3 py-1 text-sm rounded-md text-n-slate-11 hover:bg-n-alpha-1"
        @click="toggleVisible"
      >
        {{
          isVisible
            ? t('DASHBOARD.MEU_ESPACO.EVOLUTION.HIDE_BUTTON')
            : t('DASHBOARD.MEU_ESPACO.EVOLUTION.SHOW_BUTTON')
        }}
      </button>
    </div>

    <div v-if="isVisible" class="px-3 pb-3">
      <p v-if="hasError" class="text-sm text-n-ruby-9">
        {{ t('DASHBOARD.MEU_ESPACO.LOAD_ERROR') }}
        <button type="button" class="underline" @click="fetchData">
          {{ t('DASHBOARD.MEU_ESPACO.RETRY') }}
        </button>
      </p>
      <p v-else-if="isLoading" class="text-sm text-n-slate-11">
        {{ t('DASHBOARD.MEU_ESPACO.LOADING') }}
      </p>
      <p v-else-if="!series.length" class="text-sm text-n-slate-10">
        {{ t('DASHBOARD.MEU_ESPACO.EMPTY_STATE') }}
      </p>
      <div v-else class="h-64">
        <LineChart
          :collection="chartCollection"
          :chart-options="chartOptions"
        />
      </div>
    </div>
  </section>
</template>
